// src/services/aeo/monthlyReport.ts
//
// The monthly report and "Send to client" (ourteam-backend#6426, child 6/6).
//
//   getMonthlyReport(businessId)    GET  /businesses/{id}/monthly-report
//   sendMonthlyReport(businessId)   POST /businesses/{id}/monthly-report/send
//
// A client reads the copy that was SENT, and only while its check is
// published: `null` until staff send one. Staff read a live preview of the
// newest finished check, with `published` and `sentAt`. Sending publishes that
// check through the existing gate and tells the client (bell and email); a
// second send changes nothing and tells nobody twice.
//
// The report is built from whichever cards have data. A card with none is
// ABSENT, never a zero.

import axios from "axios";
import { getCookie } from "@util/cookie";
import { catchErrorLog } from "@util/sentry";
import type { AeoMonthSummary, AeoTraffic } from "./types";

const apiURL = import.meta.env.VITE_TALENT_AI_BE_BASE_URL;
const AEO_BASE = `${apiURL}/api/v1`;

export interface MonthlyReportMover {
    query: string;
    /** Average position at the start of the window, and now. Lower is better. */
    was: number;
    now: number;
    clicks: number | null;
}

export interface MonthlyReport {
    business: { id: string; name: string; domain: string };
    assessmentId: string;
    checkedAt: string | null;
    /** Only when it was written from these very figures; otherwise the template line is used. */
    monthSummary?: AeoMonthSummary;
    search?: {
        window?: { start: string; end: string; days: number } | null;
        totals: {
            clicks: number | null;
            clicksDeltaPct: number | null;
            impressions: number | null;
            impressionsDeltaPct: number | null;
        };
        moved?: { up: MonthlyReportMover[]; slipped: MonthlyReportMover[] };
        closeToPageOne?: { query: string; position: number; impressions: number | null }[];
        weekly?: { weekStart: string; clicks: number; impressions: number }[];
    };
    traffic?: AeoTraffic;
    /** Staff preview only: whether the check is published to the client. */
    published?: boolean;
    /** When this check's report went to the client; null on a staff preview not sent yet. */
    sentAt?: string | null;
}

export type ReportRead = { ok: true; report: MonthlyReport | null } | { ok: false; message: string };
export type SendResult =
    | { ok: true; alreadySent: boolean; notice: string }
    | { ok: false; message: string };

function authHeaders(): Record<string, string> {
    const token = getCookie("authToken");
    return token ? { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } : { "Content-Type": "application/json" };
}

function refusal(err: unknown, where: string): { ok: false; message: string } {
    const res = (err as { response?: { status?: number; data?: { message?: string } } }).response;
    if (!res) return { ok: false, message: "The server did not answer. Check your connection and try again." };
    if (!res.data?.message) catchErrorLog(`[oura] monthly report ${where} failed (${res.status ?? "no status"})`);
    return { ok: false, message: res.data?.message ?? "That did not go through. Try again." };
}

const reportUrl = (businessId: string) => `${AEO_BASE}/businesses/${encodeURIComponent(businessId)}/monthly-report`;

export async function getMonthlyReport(businessId: string): Promise<ReportRead> {
    try {
        const res = await axios.get(reportUrl(businessId), { headers: authHeaders() });
        return { ok: true, report: (res.data?.data as MonthlyReport | null) ?? null };
    } catch (err) {
        return refusal(err, "read");
    }
}

export async function sendMonthlyReport(businessId: string): Promise<SendResult> {
    try {
        const res = await axios.post(`${reportUrl(businessId)}/send`, {}, { headers: authHeaders() });
        const data = res.data?.data ?? {};
        return { ok: true, alreadySent: data.alreadySent === true, notice: String(data.notice ?? "") };
    } catch (err) {
        return refusal(err, "send");
    }
}
